import React, { memo, useState } from 'react';
import { View } from 'react-native';
import { useTheme } from '@redlink/assets/styles';
import { Space, LoadingContainer } from '@redlink/components/atoms';
import { useLocale } from '@redlink/assets/locale';
import { ChartBlockContainer } from './chartBlockContainer';
import {
  VictoryAxis,
  VictoryArea,
  VictoryChart,
  VictoryTheme,
  VictoryTooltip,
} from 'victory-native';
import { VictoryVoronoiContainer } from 'victory';
import { BodyBlockContainer } from './bodyBlockContainer';
import { useMoneyMask } from '@redlink/core';
import { ReportSummaryList } from './reportSummaryList';
import { TopTenLinkList } from './topTenLinkList';
import {
  useQuery,
  getLinksWithChartQuery,
  getLinkSummaryQuery,
} from '@redlink/services';

export const LinkBlock = memo(() => {
  const { dictionary } = useLocale();
  const { getValue } = useMoneyMask();
  const [periodType, setPeriodType] = useState('lastMonth');

  const {
    loading,
    data: { LinksWithChart = [] },
  } = useQuery(getLinksWithChartQuery, {
    variables: {
      period: periodType,
    },
  });

  const {
    loading: getSummaryLoading,
    data: {
      LinksSummary: {
        count = 0,
        countChange = 0,
        activeCount = 0,
        clickCount = 0,
        clickCountChange = 0,
        avgClickCount = 0,
        avgUserLinks = 0,
        topTen = [],
      } = {},
    },
  } = useQuery(getLinkSummaryQuery);

  const data = [
    {
      title: dictionary.linksCount,
      value: count,
      change: countChange,
      valueFormatter: (value) => getValue(value),
    },
    {
      title: dictionary.activeLinksCount,
      value: activeCount,
    },
    {
      title: dictionary.linksClickCount,
      value: clickCount,
      change: clickCountChange,
      valueFormatter: (value) => getValue(value),
    },
    {
      title: dictionary.avgLinksClickCount,
      value: avgClickCount,
    },
    {
      title: dictionary.avgLinksPerUser,
      value: avgUserLinks,
    },
  ];

  return (
    <ChartBlockContainer
      title={dictionary.linksReport}
      selectedPeriodTypeKey={periodType}
      onChangePeriodTypeKey={(i) => setPeriodType(i)}
    >
      <LoadingContainer isBusy={loading}>
        <LinkChart {...{ LinksWithChart }} />
        <Space />
      </LoadingContainer>
      <LoadingContainer isBusy={getSummaryLoading}>
        <BodyBlockContainer>
          <ReportSummaryList {...{ data }} />
          <TopTenLinkList data={topTen} />
        </BodyBlockContainer>
      </LoadingContainer>
    </ChartBlockContainer>
  );
});

const LinkChart = memo(({ LinksWithChart }) => {
  const { colors } = useTheme();
  const [width, setWidth] = useState(0);

  return (
    <View
      onLayout={({
        nativeEvent: {
          layout: { width },
        },
      }) => setWidth(width)}
    >
      <VictoryChart
        height={300}
        width={width}
        theme={VictoryTheme.material}
        containerComponent={<VictoryVoronoiContainer voronoiDimension="x" />}
      >
        <VictoryAxis fixLabelOverlap />
        <VictoryAxis dependentAxis tickFormat={(y) => (y % 1 === 0 ? y : '')} />
        <VictoryArea
          style={{ data: { fill: colors.blue4 } }}
          data={LinksWithChart.map(({ label: x, value: y }) => ({ x, y }))}
          labels={({ datum }) => datum.y}
          labelComponent={<VictoryTooltip orientation="top" />}
        />
      </VictoryChart>
    </View>
  );
});
